import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { CLIENT_NAV } from "@/lib/client-nav";
import { PortalPage } from "@/components/portal/PortalPage";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Calendar, Download, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { SlotPicker } from "@/components/book/SlotPicker";
import { useServerFn } from "@tanstack/react-start";
import { cancelAppointment } from "@/lib/booking.functions";
import { toast } from "sonner";

export const Route = createFileRoute("/client/appointments")({
  head: () => ({ meta: [{ title: "Appointments — Command Center" }, { name: "robots", content: "noindex" }] }),
  component: () => (
    <ProtectedRoute requiredRole="client">
      <PortalShell title="Family Command Center" nav={CLIENT_NAV} tourKey="client_tour">
        <Appointments />
      </PortalShell>
    </ProtectedRoute>
  ),
});

interface Appt { id: string; scheduled_at: string; duration_minutes: number; status: string; notes: string | null; }

function ics(a: Appt) {
  const start = new Date(a.scheduled_at);
  const end = new Date(start.getTime() + a.duration_minutes * 60000);
  const fmt = (d: Date) => d.toISOString().replace(/[-:]|\.\d{3}/g, "");
  const content = `BEGIN:VCALENDAR\nVERSION:2.0\nBEGIN:VEVENT\nUID:${a.id}\nSUMMARY:Currency Thoughts Strategist Meeting\nDTSTART:${fmt(start)}\nDTEND:${fmt(end)}\nDESCRIPTION:${a.notes ?? ""}\nEND:VEVENT\nEND:VCALENDAR`;
  const blob = new Blob([content], { type: "text/calendar" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url; link.download = `currency-thoughts-${a.id}.ics`; link.click();
  URL.revokeObjectURL(url);
}

function Appointments() {
  const { user } = useAuth();
  const [list, setList] = useState<Appt[]>([]);
  const [open, setOpen] = useState(false);
  const cancel = useServerFn(cancelAppointment);

  async function load() {
    if (!user) return;
    const { data: client } = await supabase.from("clients").select("id").eq("user_id", user.id).maybeSingle();
    if (!client) return;
    const { data } = await supabase.from("appointments").select("id,scheduled_at,duration_minutes,status,notes").eq("client_id", client.id).order("scheduled_at", { ascending: false });
    setList((data ?? []) as Appt[]);
  }

  useEffect(() => { void load(); }, [user?.id]);

  async function onCancel(id: string) {
    const res = await cancel({ data: { appointmentId: id } });
    if (res.ok) { toast.success("Appointment cancelled. That time is open again."); void load(); }
    else toast.error(res.message ?? "Could not cancel.");
  }

  const now = Date.now();
  const upcoming = list.filter(a => new Date(a.scheduled_at).getTime() >= now && a.status !== "cancelled");
  const past = list.filter(a => new Date(a.scheduled_at).getTime() < now || a.status === "cancelled");

  return (
    <PortalPage eyebrow="Stewardship Calendar" title="Appointments" intro="Past and upcoming meetings with your Strategist." actions={
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild><PrimaryButton>Book a meeting</PrimaryButton></DialogTrigger>
        <DialogContent className="bg-cream-50 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-navy-800">Schedule with your Strategist</DialogTitle></DialogHeader>
          <SlotPicker
            authenticated
            defaultEmail={user?.email ?? ""}
            defaultName={(user?.user_metadata?.full_name as string) ?? ""}
            compact
            onBooked={() => { void load(); }}
          />
        </DialogContent>
      </Dialog>
    }>
      <Section title="Upcoming" rows={upcoming} cancellable />
      <div className="mt-10"><Section title="Past" rows={past} /></div>
    </PortalPage>
  );

  function Section({ title, rows, cancellable }: { title: string; rows: Appt[]; cancellable?: boolean }) {
    return (
      <div>
        <h3 className="font-display text-navy-800 text-[20px] mb-4">{title}</h3>
        {rows.length === 0 ? <p className="font-sans text-[13px] text-charcoal-500">No meetings.</p> : (
          <div className="space-y-3">
            {rows.map(a => (
              <div key={a.id} className="bg-white border border-bronze-600/20 rounded-[4px] p-5 shadow-soft flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Calendar className="w-5 h-5 text-bronze-600" />
                  <div>
                    <p className="font-display text-navy-800">{new Date(a.scheduled_at).toLocaleString()}</p>
                    <p className="font-sans text-[12px] text-charcoal-500">{a.duration_minutes} min · {a.status}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <button onClick={() => ics(a)} className="flex items-center gap-1.5 text-bronze-600 font-sans text-[12px] uppercase tracking-wider"><Download className="w-4 h-4" />ICS</button>
                  {cancellable && (
                    <button onClick={() => onCancel(a.id)} className="flex items-center gap-1.5 text-charcoal-500 font-sans text-[12px] uppercase tracking-wider hover:text-danger"><X className="w-4 h-4" />Cancel</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
}
